import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { SliceThunkAction } from "./type-utils"
import { allFiltersCleared, searchFulfilled } from "./search-actions"

type PaginationState = {
  queryFrom: number
  querySize: number
  resultCount: number
}

const calculatePageCount = (querySize: number, resultCount: number) =>
  resultCount === 0 ? 1 : Math.ceil(resultCount / querySize)

export const paginationSlice = createSlice({
  name: "pagination",
  initialState: {
    queryFrom: 0,
    querySize: 20,
    resultCount: 0,
  } as PaginationState,

  reducers: {
    queryFromChanged(state, { payload: from }: PayloadAction<number>) {
      state.queryFrom = from
    },

    querySizeChanged(state, { payload: size }: PayloadAction<number>) {
      state.querySize = size
    },
  },

  extraReducers(builder) {
    builder.addCase(searchFulfilled, (state, action) => {
      const { total: resultCount } = action.payload
      const { querySize, queryFrom } = state
      if (resultCount <= queryFrom) {
        const pageCount = calculatePageCount(querySize, resultCount) - 1
        state.queryFrom = pageCount * querySize
      }
      state.resultCount = resultCount
    })

    builder.addCase(allFiltersCleared, (state) => {
      state.queryFrom = 0
    })
  },

  selectors: {
    selectQueryPagination: createSelector(
      [(state) => state.queryFrom, (state) => state.querySize],
      (queryFrom, querySize) => ({ from: queryFrom, size: querySize }),
    ),

    selectPageNumber: ({ queryFrom, querySize }) =>
      Math.ceil(queryFrom / querySize + 1),

    selectPageStart: ({ queryFrom, resultCount }) =>
      resultCount === 0 ? 0 : queryFrom + 1,

    selectPageEnd: ({ queryFrom, querySize, resultCount }) =>
      Math.min(queryFrom + querySize, resultCount),

    selectPageSize: ({ queryFrom, querySize, resultCount }) =>
      Math.min(querySize, resultCount - queryFrom),

    selectPageCount: ({ querySize, resultCount }) =>
      calculatePageCount(querySize, resultCount),

    selectResultCount: ({ resultCount }) => resultCount,
  },
})

type PaginationThunkAction = SliceThunkAction<typeof paginationSlice>

function setPage(page: number): PaginationThunkAction {
  return (dispatch, getState) => {
    const { querySize } = paginationSlice.selectSlice(getState())
    const queryFrom = (page - 1) * querySize

    return dispatch(queryFromChanged(queryFrom))
  }
}

const { queryFromChanged, querySizeChanged } = paginationSlice.actions
export const paginationActions = {
  querySizeChanged,
  setPage,
}

const {
  selectQueryPagination,
  selectPageNumber,
  selectPageStart,
  selectPageEnd,
  selectPageSize,
  selectPageCount,
  selectResultCount,
} = paginationSlice.selectors

const selectResultsPagination = createSelector(
  [
    selectPageNumber,
    selectPageStart,
    selectPageEnd,
    selectPageSize,
    selectPageCount,
    selectResultCount,
  ],
  (pageNumber, pageStart, pageEnd, pageSize, pageCount, resultCount) => ({
    pageNumber,
    pageStart,
    pageEnd,
    pageSize,
    pageCount,
    resultCount,
  }),
)

export const paginationSelectors = {
  selectResultsPagination,
  selectQueryPagination,
}
